/* ============================================================
 * 要素式文书接口
 * ============================================================ */
import http from "../utils/http";
import FileHandler from "../utils/fileHandler";

// 示例描述内容（generateResults 直接访问/刷新丢快照时的本地兜底案例）
export const EXAMPLE_CASE = `我想起诉一个朋友还钱。

2025年3月12日，王某因为做生意资金周转向我借了20万元，当时我们签了一张借条，约定借款期限6个月，2025年9月12日前还清，利息按照年利率6%计算。我当天通过招商银行转账20万元到他的银行卡，转账记录还在。

借款到期以后，他一直说资金紧张没有还。2025年11月他先还了2万元，之后就没有再还过。我通过微信和电话催过很多次，他也承认还欠我18万元，并说会尽快还，但是到现在一直没有履行。

王某目前住在上海市浦东新区。我手里有借条、银行转账记录、微信聊天记录以及他归还2万元的转账记录。

我现在想起诉要求他归还剩余18万元借款，并支付约定的利息。请根据这些情况帮我生成一份民间借贷纠纷的要素式起诉状。`;

// ---------- 创建对话 ----------
// AIChats/Add，不返回实体，需回查列表拿 id
export const addElementChat = (data) => {
  return http.post("/api/AIChats/Add", data);
};

// ---------- 我的对话分页 ----------
export const getMyElementChatsPage = (params) => {
  return http.post("/api/AIChats/GetMyPageData", params);
};

// ---------- 示例描述 ----------
// GET /api/IndictmentConversation/Sample
/**
 * 获取要素式文书示例描述
 * @param {string} [type] 示例类型，当前固定「民间借贷示例」
 */
export const getElementExample = (type = "民间借贷示例") => {
  return http.get("/api/IndictmentConversation/Sample", {
    params: { type },
  });
};

// ---------- 持续交互 ----------
// AIChatHistorys/Add，返回 data 为 AI 回复记录，含 content
export const addElementChatHistory = (data, signal) => {
  return http.post("/api/AIChatHistorys/Add", data, { signal });
};

// ---------- 聊天记录 ----------
// POST /api/AIChatHistorys/GetPageData，whereId 为记录的 aiChatsId（非记录 id）；
// 返回 data.data 为消息数组（时间倒序），isSend=true 为用户消息，false 为 AI
export const getElementChatHistoryPage = (params) => {
  return http.post("/api/AIChatHistorys/GetPageData", params);
};

// ---------- 雪花 ID ----------
// GET /api/base/GetSnowflakeId（无入参）
/**
 * 获取雪花 ID
 */
export const getSnowflakeId = () => {
  return http.get("/api/base/GetSnowflakeId");
};

// ---------- 最近文书 ----------
// GET /api/IndictmentConversation/Recent?Count=x
/**
 * 获取当前用户最近的要素式文书生成记录
 * @param {number} [count=3] 返回条数
 */
export const getRecentIndictmentConversations = (count = 3) => {
  return http.get("/api/IndictmentConversation/Recent", {
    params: { Count: count },
  });
};

// ---------- 全部文书分页 ----------
// GET /api/IndictmentConversation/Page?PageIndex=1&PageSize=10
/**
 * 分页获取当前用户的要素式文书生成记录
 * @param {Object} params
 * @param {number} params.pageIndex  页码，从 1 开始
 * @param {number} params.pageSize   每页条数
 * @param {string} [params.keyword]  文书名称搜索关键词（后端支持时生效）
 */
export const getIndictmentConversationPage = ({
  pageIndex = 1,
  pageSize = 10,
  keyword,
} = {}) => {
  return http.get("/api/IndictmentConversation/Page", {
    params: {
      PageIndex: pageIndex,
      PageSize: pageSize,
      ...(keyword ? { Keyword: keyword } : {}),
    },
  });
};

// ---------- 删除文书 ----------
// POST /api/IndictmentConversation/Delete?id=1
/**
 * 删除要素式文书生成记录
 * @param {string|number} id 记录 ID
 */
export const deleteIndictmentConversation = (id) => {
  return http.post(`/api/IndictmentConversation/Delete?id=${id}`);
};

// ---------- 下载文书 ----------
// GET /api/IndictmentConversation/Download?id=11
/**
 * 下载要素式文书文件
 * @param {string|number} id 记录 ID
 * @returns {Promise<Blob>}
 */
export const downloadIndictmentConversation = (id) => {
  return http.get("/api/IndictmentConversation/Download", {
    params: { id },
    responseType: "blob",
    // 跳过拦截器自动落盘，由调用方用 FileHandler.saveFile 保存
    skipFileHandler: true,
  });
};

/**
 * 下载要素式文书并保存到本地
 * @param {{ id: string|number, name?: string }} item
 */
export const saveIndictmentDoc = async (item) => {
  const blob = await downloadIndictmentConversation(item.id);
  const base = item.name || "要素式文书";
  const lower = base.toLowerCase();
  const hasExt =
    lower.endsWith(".doc") || lower.endsWith(".docx") || lower.endsWith(".pdf");
  FileHandler.saveFile(blob, hasExt ? base : `${base}.docx`);
};

// ---------- 查看详情 ----------
// GET /api/IndictmentConversation/Detail?id=1
/**
 * 获取要素式文书交互记录详情
 * @param {string|number} id 记录 ID
 */
export const getIndictmentConversationDetail = (id) => {
  return http.get("/api/IndictmentConversation/Detail", {
    params: { id },
  });
};

// ---------- 材料上传生成 ----------
// POST /api/IndictmentConversation/GenerateFromMaterial
// 支持 Word（doc/docx）、PDF、图片（jpg/jpeg/png）
/**
 * 上传案件材料并启动要素式文书生成
 * @param {File|File[]} files  单个或多个材料文件
 * @param {(percent:number)=>void} [onProgress] 上传进度回调
 */
export const generateIndictmentFromMaterial = (files, onProgress) => {
  const formData = new FormData();
  const list = Array.isArray(files) ? files : [files];
  list.forEach((f) => {
    if (f) formData.append("file", f);
  });

  // 不要手动设 Content-Type，axios 检测到 FormData 会自动带 boundary
  return http.post(
    "/api/IndictmentConversation/GenerateFromMaterial",
    formData,
    {
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          onProgress(
            Math.round((progressEvent.loaded / progressEvent.total) * 100),
          );
        }
      },
    },
  );
};
